import * as React from 'react'

import {
  Box,
  CheckBox,
  Icon,
  Link,
  Table,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import UserAvatar from 'modules/core/components/UserAvatar'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { InjectHoverProvider } from 'modules/core/lib/hoverContext'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import ShowText from 'modules/payroll_bundle/components/common/components/ShowText'
import getUserByItem from 'modules/payroll_bundle/components/common/helper/getUserByItem'
import ShowAmount from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage/_components/_ShowAmount'
import { disablePaymentItem } from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/disableRowItem'
import type { PaymentItemLikeMinimal } from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/PaymentsPage/GeneratePaymentsPage/Table/useDraftPaymentItemsDataCollectionSource'

interface Props {
  paymentItem: PaymentItemLikeMinimal
  onSelectItem: (payload: PaymentItemLikeMinimal) => void
  checkable: boolean
  selected: Set<number>
}

const AlertIcon = () => {
  return <Icon.Medium color="radicalRed1200" icon="alert-circle" />
}

const getPaymentItemEmployee = (
  paymentItem: PaymentItemLikeMinimal
): Employee | null | undefined => {
  if (!paymentItem.has('employee_id')) return null
  const employeeId = paymentItem.attributes?.employee_id
  return employeeId != null ? employees.get(employeeId) : null
}

const getPaymentItemFullName = (paymentItem: PaymentItemLikeMinimal): string => {
  const employee = getPaymentItemEmployee(paymentItem)
  if (employee) {
    const user = users.get(employee.attributes.access_id)
    if (user)
      return getFullDisplayName({
        firstName: user.attributes.first_name,
        lastName: user.attributes.last_name,
        canSharePreferredName: employee.policy.can('sharePreferredName'),
        preferredName: employee.attributes.preferred_name,
      })
  }
  return paymentItem.attributes?.full_name ?? ''
}

const Item = ({ paymentItem, onSelectItem, checkable, selected }: Props) => {
  const user = getUserByItem(paymentItem)
  const disabled = disablePaymentItem(paymentItem, user)
  const checked = selected.has(paymentItem.id)
  const [open, toggle] = useToggle(false)
  const { navigation } = useNavigation()

  const ref = React.useRef<HTMLParagraphElement>(null)

  const currentRef = ref?.current

  const isOverflowing = currentRef && currentRef.offsetWidth < currentRef.scrollWidth

  const TableCheckBox = () => {
    if (!user) return null

    return (
      <Box width="s32">
        {disabled ? (
          <AlertIcon />
        ) : (
          <CheckBox checked={!!checked} onClick={() => onSelectItem && onSelectItem(paymentItem)} />
        )}
      </Box>
    )
  }

  return (
    <Table.Row>
      <Table.Cell wide>
        <Box flexDirection="row" gap="s12" alignItems="center">
          {checkable && (
            <Box width="s40">
              <TableCheckBox />
            </Box>
          )}
          {user && <UserAvatar size="s40" user={user} />}
          <Box width="s360">
            <Tooltip.Alt
              attachment="top left"
              targetAttachment="middle right"
              size="small"
              open={!!isOverflowing && open}
              toggle={toggle}
              label={
                paymentItem.has('full_name')
                  ? (paymentItem.attributes?.full_name ?? '')
                  : getPaymentItemFullName(paymentItem)
              }
            >
              <InjectHoverProvider>
                <Text.Normal ellipsis weight="medium" color="grey1000" innerRef={ref}>
                  <Link.Router
                    to={navigation.employees.id.personal.fullPath({
                      id: paymentItem.has('employee_id') && paymentItem.attributes?.employee_id != null
                        ? String(paymentItem.attributes.employee_id)
                        : getPaymentItemEmployee(paymentItem)?.id.toString() ?? '',
                    })}
                  >
                    {paymentItem.has('full_name')
                      ? (paymentItem.attributes?.full_name ?? '')
                      : getPaymentItemFullName(paymentItem)}
                  </Link.Router>
                </Text.Normal>
              </InjectHoverProvider>
            </Tooltip.Alt>
          </Box>
        </Box>
      </Table.Cell>
      <Table.Cell width={200}>
        <ShowAmount
          amount={String((paymentItem.attributes?.amount ?? 0) / 100)}
        />
      </Table.Cell>
      <Table.Cell>
        <ShowText
          iban
          label={i18n.t('payroll_bundle.results_payments.payments.table.invalid_iban')}
          text={paymentItem.attributes?.bank_account ?? ''}
        />
      </Table.Cell>
    </Table.Row>
  )
}

export default observer(Item)
